import React, { useState } from 'react';
import axios, { API } from '../lib/api';
import AIFeedbackSection from './AIFeedbackSection';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';

const DailyTaskCard = ({ task, onTaskUpdate }) => {
  const [reflection, setReflection] = useState(task.reflection || '');
  const [timeSpent, setTimeSpent] = useState(task.time_spent || '');
  const [showReflection, setShowReflection] = useState(task.completed);
  const savedReflection = (task.reflection || '').trim();

  const handleComplete = async (completed) => {
    try {
      const updateData = { completed };
      if (completed) {
        setShowReflection(true);
      } else {
        setShowReflection(false);
        updateData.reflection = null;
        updateData.time_spent = null;
      }
      await axios.put(`${API}/tasks/${task.id}`, updateData);
      onTaskUpdate();
      toast.success(completed ? 'Task completed!' : 'Task marked incomplete');
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleReflectionSubmit = async () => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        completed: true,
        reflection,
        time_spent: timeSpent ? parseInt(timeSpent) : null,
      });
      onTaskUpdate();
      toast.success('Reflection saved!');
    } catch {
      toast.error('Failed to save reflection');
    }
  };

  return (
    <Card className="mb-4 hover:shadow-md transition-shadow duration-300">
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          <Checkbox checked={task.completed} onCheckedChange={handleComplete} className="mt-1" />
          <div className="flex-1">
            <p className={`font-medium ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
              {task.task_description}
            </p>

            {showReflection && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <div>
                  <Label htmlFor={`reflection-${task.id}`} className="text-sm font-medium text-justb-primary">
                    Reflection (What did you learn or accomplish?)
                  </Label>
                  <Textarea
                    id={`reflection-${task.id}`}
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    placeholder="Reflect on your progress, what worked well, and what you learned..."
                    rows={2}
                    className="mt-1 text-gray-900"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <Label htmlFor={`time-${task.id}`} className="text-sm font-medium text-justb-secondary">
                      Time Spent (minutes)
                    </Label>
                    <Input
                      id={`time-${task.id}`}
                      type="number"
                      value={timeSpent}
                      onChange={(e) => setTimeSpent(e.target.value)}
                      placeholder="30"
                      className="mt-1 text-gray-900"
                    />
                  </div>
                  <Button onClick={handleReflectionSubmit} size="sm" className="mt-6 bg-justb-secondary hover:bg-justb-secondary/90">
                    Save
                  </Button>
                </div>

                <AIFeedbackSection
                  taskId={task.id}
                  savedReflection={savedReflection}
                  initialFeedback={task.ai_feedback || null}
                />
              </div>
            )}
          </div>

          {task.time_spent && <Badge variant="outline" className="text-justb-secondary">{task.time_spent}m</Badge>}
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyTaskCard;
